import { useState, useRef, useEffect, FormEvent } from 'react';

const SERVICES_OPTIONS = [
  '반영구 눈썹 문신 (Semi-Permanent Eyebrow)',
  '역노화 레이저 MICRO-JET (Reverse Aging Laser)',
  '안신환+청뇌환 (Herbal Medicine)',
  '슬림 다이어트 한약 (Slim Diet)',
  '아이돌 메이크업 (Idol Make-up)',
  '기타 문의 (Other)',
];

export default function ReservationSection() {
  const ref = useRef<HTMLElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll('.section-reveal');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d71pub1lum2co2q1frsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
      form.reset();
      setCharCount(0);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reservation" ref={ref} className="bg-white py-20 px-7 md:py-28 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20 section-reveal">
          <span className="inline-block text-gold font-sans text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">
            Reservation &amp; Directions · 预约 · ご予約
          </span>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 font-bold mb-4">예약 & 오시는 길</h2>
          <div className="w-16 h-0.5 bg-gold mx-auto mb-5"></div>
          <p className="font-sans text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
            온라인 예약 또는 전화로 간편하게 예약하세요. 전문 스태프가 친절하게 안내해 드립니다.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 md:gap-14">
          {/* Reservation Form */}
          <div className="section-reveal">
            <div className="bg-cream border border-gold/15 rounded-2xl p-7 sm:p-9">
              <h3 className="font-serif text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="w-6 h-6 flex items-center justify-center text-gold"><i className="ri-calendar-check-line text-xl"></i></span>
                온라인 예약 문의
              </h3>

              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 flex items-center justify-center bg-gold/10 rounded-full mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-3xl text-gold"></i>
                  </div>
                  <h4 className="font-serif text-xl font-bold text-gray-900 mb-2">예약 문의 완료!</h4>
                  <p className="font-sans text-sm text-gray-500 mb-5">빠른 시간 내에 연락 드리겠습니다.<br />Thank you! We will contact you soon.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="bg-plum text-white px-6 py-2.5 rounded-full font-sans text-sm font-semibold cursor-pointer hover:bg-plum-dark transition-colors whitespace-nowrap"
                  >
                    다시 예약하기
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="font-sans text-xs font-semibold text-gray-600 mb-1.5 block">성함 *</label>
                      <input
                        name="name"
                        required
                        placeholder="홍길동"
                        className="w-full border border-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-gold transition-colors"
                      />
                    </div>
                    <div>
                      <label className="font-sans text-xs font-semibold text-gray-600 mb-1.5 block">연락처 *</label>
                      <input
                        name="phone"
                        required
                        placeholder="010-0000-0000"
                        className="w-full border border-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-gold transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-sans text-xs font-semibold text-gray-600 mb-1.5 block">이메일</label>
                    <input
                      name="email"
                      type="email"
                      placeholder="example@email.com"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-gold transition-colors"
                    />
                  </div>

                  <div>
                    <label className="font-sans text-xs font-semibold text-gray-600 mb-1.5 block">희망 서비스 *</label>
                    <select
                      name="service"
                      required
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-gold transition-colors bg-white"
                    >
                      <option value="">서비스를 선택해주세요</option>
                      {SERVICES_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="font-sans text-xs font-semibold text-gray-600 mb-1.5 block">희망 방문 날짜</label>
                    <input
                      name="visit_date"
                      type="date"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-gold transition-colors"
                    />
                  </div>

                  <div>
                    <label className="font-sans text-xs font-semibold text-gray-600 mb-1.5 block">
                      문의 내용 <span className="text-gray-400 font-normal">({charCount}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      maxLength={500}
                      rows={4}
                      placeholder="문의 사항을 자유롭게 입력해주세요..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-gold transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-plum text-white py-3.5 rounded-full font-sans font-semibold text-sm cursor-pointer hover:bg-plum-dark transition-colors disabled:opacity-60 whitespace-nowrap"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        전송 중...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-send-plane-fill"></i>
                        예약 문의 보내기
                      </span>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Quick Contact */}
            <div className="mt-4 grid grid-cols-3 gap-3">
              <a
                href="tel:01067404061"
                className="flex flex-col items-center gap-1 bg-plum text-white rounded-xl py-4 cursor-pointer hover:bg-plum-dark transition-colors"
              >
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-phone-fill text-lg"></i></span>
                <span className="font-sans text-xs font-semibold">전화 상담</span>
                <span className="font-sans text-xs text-white/70">010-6740-4061</span>
              </a>
              <a
                href="https://open.kakao.com/o/sFFmWDmi"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex flex-col items-center gap-1 bg-[#FAE100] text-[#3A1D1D] rounded-xl py-4 cursor-pointer hover:bg-yellow-400 transition-colors"
              >
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-kakao-talk-fill text-lg"></i></span>
                <span className="font-sans text-xs font-semibold">카카오톡</span>
                <span className="font-sans text-xs text-[#3A1D1D]/60">채널 상담</span>
              </a>
              <a
                href="https://line.me/ti/p/5UmAcjQ_dg"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex flex-col items-center gap-1 bg-[#06C755] text-white rounded-xl py-4 cursor-pointer hover:bg-green-600 transition-colors"
              >
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-line-fill text-lg"></i></span>
                <span className="font-sans text-xs font-semibold">LINE</span>
                <span className="font-sans text-xs text-white/70">라인 상담</span>
              </a>
            </div>
          </div>

          {/* Map & Directions */}
          <div className="section-reveal flex flex-col gap-5">
            {/* Map Image */}
            <div className="rounded-2xl overflow-hidden border border-gold/15">
              <img
                src="https://storage.readdy-site.link/project_files/d3e20c33-90eb-45ba-8644-8f19c58dcc82/58c10494-3a45-4b66-85ac-2e769ddb288f_unnamed.png?v=dd27a0bf3d52a42d8154e9f364760606"
                alt="헤라브로우크리닉 위치 지도"
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Map Quick Links */}
            <div className="grid grid-cols-2 gap-3">
              <a
                href="https://map.naver.com/v5/search/서울시 중구 명동3길 5 위드빌딩"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#03C75A] text-white rounded-xl py-3.5 cursor-pointer hover:bg-[#02a84c] transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-2-fill text-base"></i>
                </span>
                <span className="font-sans text-sm font-semibold whitespace-nowrap">네이버 지도</span>
              </a>
              <a
                href="https://www.google.com/maps/search/서울시+중구+명동3길+5+위드빌딩"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#EA4335] text-white rounded-xl py-3.5 cursor-pointer hover:bg-[#c9352a] transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-2-line text-base"></i>
                </span>
                <span className="font-sans text-sm font-semibold whitespace-nowrap">구글 지도</span>
              </a>
            </div>

            {/* Directions Info */}
            <div className="bg-cream border border-gold/15 rounded-2xl p-6">
              <h4 className="font-serif text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-gold"><i className="ri-map-pin-2-fill"></i></span>
                오시는 길
              </h4>

              <div className="space-y-4">
                <div className="flex gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-plum rounded-full text-white flex-shrink-0">
                    <i className="ri-building-line text-sm"></i>
                  </span>
                  <div>
                    <p className="font-sans text-sm font-semibold text-gray-900">주소</p>
                    <p className="font-sans text-sm text-gray-600">서울시 중구 명동3길 5 위드빌딩 6층</p>
                    <p className="font-sans text-xs text-gray-400 mt-0.5">5 Myeongdong 3-gil, Jung-gu, Seoul, 6F With Building</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-gold rounded-full text-white flex-shrink-0">
                    <i className="ri-subway-line text-sm"></i>
                  </span>
                  <div>
                    <p className="font-sans text-sm font-semibold text-gray-900">지하철</p>
                    <p className="font-sans text-sm text-gray-600">4호선 명동역 6번 출구 도보 5분, 2호선 을지로입구역 6번 출구 도보 3분</p>
                    <p className="font-sans text-xs text-gray-400 mt-0.5">Myeongdong Station (Line 4), Exit 6, 5 min walk, Euljiro1(il)-ga Station (Line 2), Exit 6, 3 min walk</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-plum-light rounded-full text-white flex-shrink-0">
                    <i className="ri-time-line text-sm"></i>
                  </span>
                  <div>
                    <p className="font-sans text-sm font-semibold text-gray-900">영업시간</p>
                    <p className="font-sans text-sm text-gray-600">매일 11:00 ~ 21:00 (연중무휴)</p>
                    <p className="font-sans text-xs text-gray-400 mt-0.5">Open Daily 11:00~21:00 · 每日营业 · 毎日営業</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-gold-dark rounded-full text-white flex-shrink-0">
                    <i className="ri-phone-line text-sm"></i>
                  </span>
                  <div>
                    <p className="font-sans text-sm font-semibold text-gray-900">연락처</p>
                    <p className="font-sans text-sm text-gray-600">010-6740-4061</p>
                    <p className="font-sans text-xs text-gray-400 mt-0.5">한·영·중·일 상담 가능</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
